/**
 * NotificationBell — campanello notifiche con badge + dropdown ultime 5
 * Il Diario di Bordo - Frontend
 */

import { useState, useRef, useEffect } from 'react'
import { Bell, ArrowRight, MessageSquare, AtSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotificationFeed } from '../hooks/useNotificationFeed'

/**
 * Raggruppa notifiche message/mention con stesso payload.threadId in un solo
 * elemento (vedi NotificationsPage.jsx per la stessa logica nel feed completo).
 * Ritorna array misto: { kind: 'single', n } | { kind: 'group', ... }
 */
const groupNotifications = (items) => {
  const result = []
  const groupsByThread = new Map()
  for (const n of items) {
    const isChat = (n.type === 'message' || n.type === 'mention') && n.payload?.threadId
    if (!isChat) {
      result.push({ kind: 'single', n })
      continue
    }
    const tid = String(n.payload.threadId)
    const existingIdx = groupsByThread.get(tid)
    if (existingIdx === undefined) {
      const group = {
        kind: 'group',
        threadId: tid,
        threadType: n.payload?.threadType,
        threadName: n.payload?.threadName,
        senders: new Map(),
        count: 0,
        unread: 0,
        mentions: 0,
        mostRecent: n,
        items: []
      }
      groupsByThread.set(tid, result.length)
      result.push(group)
    }
    const grp = result[groupsByThread.get(tid)]
    grp.count++
    if (!n.read) grp.unread++
    if (n.type === 'mention') grp.mentions++
    const senderName = n.payload?.senderName || 'Utente'
    grp.senders.set(senderName, (grp.senders.get(senderName) || 0) + 1)
    if (new Date(n.createdAt) > new Date(grp.mostRecent.createdAt)) grp.mostRecent = n
    grp.items.push(n)
  }
  return result
}

const groupTitle = (g) => {
  // Mostra il numero di messaggi NUOVI (unread). Se tutti già letti,
  // mostra il count totale come riferimento.
  const n = g.unread > 0 ? g.unread : g.count
  const newLabel = g.unread > 0 ? 'nuov' : 'recent'
  // Esclude il fallback "Utente" (vecchie notifiche pre-fix payload) se ci
  // sono nomi reali. Se TUTTI sono "Utente", lasciamo "Utente" come unico.
  const allSenders = Array.from(g.senders.entries())
  const realSenders = allSenders.filter(([name]) => name !== 'Utente')
  const senderEntries = realSenders.length > 0 ? realSenders : allSenders

  if (g.threadType === 'channel' && g.threadName) {
    return g.unread > 0
      ? `${n} nuov${n === 1 ? 'o messaggio' : 'i messaggi'} in ${g.threadName}`
      : `${g.threadName} · ${n} messagg${n === 1 ? 'io' : 'i'}`
  }
  if (senderEntries.length === 1) {
    return g.unread > 0
      ? `${n} nuov${n === 1 ? 'o messaggio' : 'i messaggi'} da ${senderEntries[0][0]}`
      : `${senderEntries[0][0]} · ${n} messagg${n === 1 ? 'io' : 'i'}`
  }
  if (senderEntries.length === 2) {
    return g.unread > 0
      ? `${n} nuovi messaggi da ${senderEntries[0][0]} e ${senderEntries[1][0]}`
      : `${senderEntries[0][0]} e ${senderEntries[1][0]} · ${n} messaggi`
  }
  return g.unread > 0
    ? `${n} nuovi messaggi da ${senderEntries.length} persone`
    : `${senderEntries.length} persone · ${n} messaggi`
}

/**
 * dropdownPlacement:
 *   'bottom-right' (default) — sotto il bell, allineato a destra (mobile header)
 *   'top-right'              — sopra il bell, allineato a destra
 *   'right-bottom'           — a destra del bell, allineato in basso (sidebar desktop bottom)
 */
export default function NotificationBell({ className = '', dropdownPlacement = 'bottom-right' }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef(null)
  const { items, unreadCount, markRead } = useNotificationFeed()

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Raggruppa prima e poi prendi i primi 5 elementi (gruppi+singoli)
  const recent = groupNotifications(items).slice(0, 5)

  const handleClick = (n) => {
    if (!n.read) markRead(n._id)
    setOpen(false)
    if (!n.actionUrl) return
    try {
      const u = new URL(n.actionUrl, window.location.origin)
      if (u.origin !== window.location.origin) return
      // Riscrittura URL legacy verso le route consolidate dell'hub Centro
      // Comunicazioni. Le route /messaggi/* e /promemoria sono wrapper
      // redirect ma evitiamo il double-navigation rewriting direttamente.
      const isOrgRoute = window.location.pathname.startsWith('/organization')
      const base = isOrgRoute ? '/organization/notifiche' : '/notifiche'
      let target = u.pathname + u.search
      const msgMatch = u.pathname.match(/^\/(?:organization\/)?messaggi(?:\/([^/?]+))?/)
      if (msgMatch) {
        target = msgMatch[1]
          ? `${base}?tab=messaggi&thread=${encodeURIComponent(msgMatch[1])}`
          : `${base}?tab=messaggi`
      } else if (u.pathname.match(/^\/(?:organization\/)?promemoria/)) {
        target = `${base}?tab=promemoria`
      }
      navigate(target)
    } catch {}
  }

  const handleGroupClick = (group) => {
    // markRead di tutti gli items del gruppo + apri la chat dentro l'hub
    // Centro Comunicazioni (tab Messaggi con thread selezionato).
    group.items.forEach((n) => { if (!n.read) markRead(n._id) })
    setOpen(false)
    // Rispetta il routing per ruolo (Responsabile usa /organization/notifiche)
    const isOrgRoute = window.location.pathname.startsWith('/organization')
    const base = isOrgRoute ? '/organization/notifiche' : '/notifiche'
    navigate(`${base}?tab=messaggi&thread=${encodeURIComponent(group.threadId)}`)
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-label="Notifiche"
      >
        <Bell className="h-5 w-5 text-gray-700 dark:text-gray-200" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-[70vh] overflow-hidden flex flex-col ${
          dropdownPlacement === 'top-right'
            ? 'right-0 bottom-full mb-2'
            : dropdownPlacement === 'right-bottom'
              ? 'left-full bottom-0 ml-2'
              : 'right-0 top-full mt-2'
        }`}>
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Notifiche</h3>
            {unreadCount > 0 && (
              <span className="text-xs text-gray-500">{unreadCount} non lette</span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {recent.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500">Nessuna notifica</p>
            ) : (
              <ul>
                {recent.map((item) => {
                  if (item.kind === 'group') {
                    const g = item
                    const Icon = g.mentions > 0 ? AtSign : MessageSquare
                    return (
                      <li key={`grp:${g.threadId}`}>
                        <button
                          onClick={() => handleGroupClick(g)}
                          className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                            g.unread > 0 ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="relative mt-0.5 flex-shrink-0">
                              <Icon className={`h-4 w-4 ${g.mentions > 0 ? 'text-indigo-500' : 'text-blue-500'}`} />
                              {g.unread > 0 && (
                                <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                  {g.unread > 9 ? '9+' : g.unread}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {groupTitle(g)}
                              </p>
                              {g.mentions > 0 && (
                                <p className="text-xs text-indigo-600 dark:text-indigo-400">
                                  ⚠ {g.mentions} menzion{g.mentions === 1 ? 'e' : 'i'}
                                </p>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    )
                  }
                  const n = item.n
                  return (
                    <li key={n._id}>
                      <button
                        onClick={() => handleClick(n)}
                        className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                          !n.read ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && (
                            <span className="mt-1.5 w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {n.title}
                            </p>
                            {n.body && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{n.body}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <button
            onClick={() => {
              setOpen(false)
              const isOrgRoute = window.location.pathname.startsWith('/organization')
              navigate(isOrgRoute ? '/organization/notifiche' : '/notifiche')
            }}
            className="p-3 border-t border-gray-200 dark:border-gray-700 text-sm text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-center gap-1"
          >
            Apri Centro Comunicazioni <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}
